import { FC, useEffect, useState, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import LoadingPanel from "../../../custom-components/CustomLoader";
import { NullableCollection, ResponseMessageObj, defaultResponseMessageObj } from "../../../types/ApiResults";
import { DEFAULT_PAGE } from "../../../domain/constants";
import { PaginationEnum } from "../../../domain/enums/common/PaginationEnum";
import { ResponseMessage } from "../../../domain/enums/common/ResponseMessageEnum";
import Toast from "../../../custom-components/Toast";
import Pagination from "../../../custom-components/CustomPagination";
import RolesTable from "./RolesTable";
import RolesHeader from "./RolesHeader";
import useCustomFormik from "../../../custom-hooks/useCustomFormik";
import { SelectValue } from "../../../types/SelectProps";
import { useTranslation } from "react-i18next";
import { RolesCollection } from "../../../domain/models/Role";
import { RoleDTO } from "../../../domain/models/dto/RoleDTO";
import {
    rolesSearchFiltersFields,
    rolesSearchFiltersSchemaFactory,
} from "../../../validation-schemas/RoleSearchFiltersFielsd";
import { roleServiceFactory } from "../../../services/RoleServiceImpl";
import syncServiceFactory from "../../../services/SyncServiceImpl";

const roleService = roleServiceFactory();
export type PickRoleName = Pick<RoleDTO, "name">;

const RolesPage: FC = () => {
    const { t } = useTranslation();
    const [roles, setRoles] = useState<RolesCollection>(NullableCollection);

    const [selectedRole, setSelectedRole] = useState<RoleDTO>(null);

    const [reloadPage, setReloadPage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
    const [currentLimit, setCurrentLimit] = useState(PaginationEnum.SHOW_20);
    const [messageObj, setMessageObj] = useState<ResponseMessageObj>(defaultResponseMessageObj);

    const fetchRoles = useCallback(
        async (filterValues?: PickRoleName, showNotification = true) => {
            setLoading(true);
            const rolesResult = await roleService.getAllRoles(currentPage, currentLimit, filterValues);
            if (rolesResult) {
                setRoles(rolesResult);
                resetSelectedRole();
                setReloadPage(false);
                if (showNotification) {
                setMessageObj({
                    id: uuidv4(),
                    messageType: ResponseMessage.SUCCESS,
                    messageKey: "messages.rolesReceived",
                });
                }
            } else {
                setMessageObj({
                    id: uuidv4(),
                    messageType: ResponseMessage.DANGER,
                    messageKey: "errors.common",
                });
            }
            setLoading(false);
        },
        [currentPage, currentLimit]
    );

    const [searchFiltersFields, setSearchFiltersFields] = useState<PickRoleName>({} as PickRoleName);
    const searchFiltersForm = useCustomFormik<PickRoleName>({
        initial: null,
        fields: rolesSearchFiltersFields,
        schema: rolesSearchFiltersSchemaFactory(t),
        onSubmit: async (values) => {
            setSearchFiltersFields(values);
            setCurrentPage(DEFAULT_PAGE);
            await fetchRoles(values);
        },
    });

    useEffect(() => {
        fetchRoles(searchFiltersFields);
    }, [fetchRoles, searchFiltersFields, reloadPage]);

    useEffect(() => {
        const syncService = syncServiceFactory();
        const subscription = syncService.refetchRoles$.subscribe(() => {
            fetchRoles(searchFiltersFields, false);
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [fetchRoles, searchFiltersFields]);

    const limitHandler = (value: SelectValue) => {
        setCurrentLimit(value as number);
    };

    const pageHandler = (value: number) => {
        setCurrentPage(value);
    };

    const resetSelectedRole = () => {
        setSelectedRole(null);
    };

    const resetMessageObj = () => {
        setMessageObj(defaultResponseMessageObj);
    };

    const resetFiltersHandler = () => {
        searchFiltersForm.form.resetForm();
        setSearchFiltersFields({} as PickRoleName);
        setReloadPage(true);
    };

    return (
        <div className="relative z-0 bg-light-bg-700 dark:bg-dark-bg-800">
            <LoadingPanel isVisible={loading} />
            <RolesHeader
                selectedRole={selectedRole}
                searchFiltersForm={searchFiltersForm.form}
                filtersFields={searchFiltersFields}
                resetSelectedRole={resetSelectedRole}
                limitHandler={limitHandler}
                resetFiltersHandler={resetFiltersHandler}
            />
            <RolesTable roles={roles.rows} updateHandler={setSelectedRole} />
            <Pagination currentPage={currentPage} totalPages={roles.totalPages} pageHandler={pageHandler} />
            <Toast messageObj={messageObj} resetMessageObj={resetMessageObj} />
        </div>
    );
};

export default RolesPage;
